import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import Api from "../../../shared/api/api";
import useCheckoutResult from "../../../shared/hooks/useCheckoutResult";
import { bookDescriptionExcerpt } from "../helpers/bookDescription";
import sortBooksBySeries from "../helpers/sortBooks";
import "../styles/BooksPage.css";

export default function BooksPage() {
  const location = useLocation();

  const [books, setBooks] = useState([]);
  const [status, setStatus] = useState("loading");
  const checkoutStatus = useCheckoutResult(location.search);

  useEffect(() => {
    let isMounted = true;

    async function fetchBooks() {
      try {
        setStatus("loading");

        const response = await Api.getAllAuthor("books");

        if (!isMounted) return;

        const bookRecords = Array.isArray(response)
          ? response
          : response?.books || response?.data || [];

        setBooks(Array.isArray(bookRecords) ? sortBooksBySeries(bookRecords) : []);
        setStatus("success");
      } catch (error) {
        if (!isMounted) return;

        console.error("Error fetching books:", error);
        setBooks([]);
        setStatus("error");
      }
    }

    fetchBooks();

    return () => {
      isMounted = false;
    };
  }, []);

  function getBookUrl(book) {
    if (!book?.title) return "/books";

    return `/books/${encodeURIComponent(book.title)}`;
  }

  function getBookDescription(book) {
    const description = bookDescriptionExcerpt(book?.description, 200);

    if (!description) {
      return "Description coming soon.";
    }

    return description;
  }

  function getBookSeries(book) {
    const series = book?.series || book?.book_series;
    const bookNumber = book?.book_number;

    if (series && bookNumber) {
      return `${series} — Book ${bookNumber}`;
    }

    if (series) {
      return series;
    }

    if (bookNumber) {
      return `Book ${bookNumber}`;
    }

    return null;
  }

  return (
    <main className="books-page-container">
      {checkoutStatus === "success" && (
        <div
          className="checkout-success-message"
          role="status"
          aria-live="polite"
        >
          Thank you! Your order was placed.
        </div>
      )}

      {(checkoutStatus === "verifying" || checkoutStatus === "processing") && (
        <div className="checkout-success-message" role="status" aria-live="polite">
          {checkoutStatus === "verifying"
            ? "Confirming your order..."
            : "Payment received. Your order is still processing; your confirmation email will arrive shortly."}
        </div>
      )}

      {checkoutStatus === "error" && (
        <div className="checkout-cancel-message" role="alert">
          We could not verify that payment was completed. Your cart has been preserved.
        </div>
      )}

      {checkoutStatus === "cancel" && (
        <div
          className="checkout-cancel-message"
          role="status"
          aria-live="polite"
        >
          Checkout was canceled.
        </div>
      )}

      <h1 className="books-page-title">Books</h1>

      {status === "loading" && (
        <p className="loadingMsg" role="status">
          Loading books...
        </p>
      )}

      {status === "error" && (
        <p className="warningError" role="alert">
          Books could not be loaded. Please try again later.
        </p>
      )}

      {status === "success" && books.length === 0 && (
        <p className="books-empty-message">
          No books are currently available.
        </p>
      )}

      {status === "success" && books.length > 0 && (
        <section className="books-grid" aria-label="Available books">
          {books.map((book) => {
            const bookUrl = getBookUrl(book);
            const seriesText = getBookSeries(book);
            const bookKey =
              book?.id ||
              book?._id ||
              book?.isbn ||
              `${book?.title || "untitled"}-${book?.book_number || "book"}`;

            return (
              <article className="book-card" key={bookKey}>
                {book?.image ? (
                  <Link
                    to={bookUrl}
                    aria-label={`View details for ${book.title || "this book"}`}
                  >
                    <img
                      src={book.image}
                      alt={`Cover of ${book.title || "book"}`}
                      className="book-card-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>
                ) : (
                  <div
                    className="book-card-image book-card-image-placeholder"
                    aria-hidden="true"
                  >
                    No cover available
                  </div>
                )}

                <div className="book-card-content">
                  <h2 className="book-card-title">
                    {book?.title || "Untitled Book"}
                  </h2>

                  {seriesText && (
                    <p className="book-card-series">{seriesText}</p>
                  )}

                  <p className="book-card-description">
                    {getBookDescription(book)}
                  </p>

                  {book?.title && (
                    <Link to={bookUrl} className="book-card-button">
                      Read More
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </main>
  );
}
